import { adminApi, parseApiError } from './api';
import type { UsageSummary, UsageByType, UsageByDay, TopUser } from '@/types/admin.types';

const defaultSummary: UsageSummary = {
  totalPhotos: 0,
  totalAudios: 0,
  totalTexts: 0,
  storageUsedBytes: 0,
  period: { start: '', end: '' },
};

export const usageService = {
  async getSummary(period?: string): Promise<UsageSummary> {
    try {
      const { data } = await adminApi.get<UsageSummary>('/admin/usage/summary', {
        params: period ? { period } : undefined,
      });
      return data || defaultSummary;
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        return defaultSummary;
      }
      throw err;
    }
  },

  async getByType(period?: string): Promise<UsageByType> {
    try {
      const { data } = await adminApi.get<UsageByType>('/admin/usage/by-type', {
        params: period ? { period } : undefined,
      });
      return data || {
        photos: { count: 0, percentage: 0 },
        audios: { count: 0, percentage: 0 },
        texts: { count: 0, percentage: 0 },
      };
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        return {
          photos: { count: 0, percentage: 0 },
          audios: { count: 0, percentage: 0 },
          texts: { count: 0, percentage: 0 },
        };
      }
      throw err;
    }
  },

  async getByDay(period?: string): Promise<UsageByDay[]> {
    try {
      const { data } = await adminApi.get<{ usage: UsageByDay[] }>('/admin/usage/by-day', {
        params: period ? { period } : undefined,
      });
      return data?.usage || [];
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        return [];
      }
      throw err;
    }
  },

  async getTopUsers(limit?: number, period?: string): Promise<TopUser[]> {
    try {
      const { data } = await adminApi.get<{ users: TopUser[] }>('/admin/usage/top-users', {
        params: { limit: limit || 10, ...(period ? { period } : {}) },
      });
      return data?.users || [];
    } catch (error) {
      const err = parseApiError(error);
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        return [];
      }
      throw err;
    }
  },
};

export default usageService;
